import {
  autoSignIn,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  rememberDevice,
  resendSignUpCode,
  signIn,
} from "aws-amplify/auth";
import React, { FormEvent, useEffect, useRef, useState } from "react";
import "../../amplifyClient";
import { Button, cn } from "@shared/ui";
import { Field, inputClassName } from "../../components/Field";
import PhoneVerificationHeader from "../../components/PhoneVerificationHeader";
import ResendCodeButton from "../../components/ResendCodeButton";
import StepBody from "../../components/StepBody";
import StepFooter from "../../components/StepFooter";
import { DEFAULT_ERROR_MESSAGE, SKIP_PASSPORT } from "../../constants";
import {
  handleReturningUserSignIn,
  updateCognitoUserAccounts,
} from "../../lib/authUtility";
import { verifyDeviceToken } from "../../lib/axle";
import { getNextStepBeforeSuccess } from "../../lib/utility";
import { RuxTokenData } from "../WelcomeBack";

interface EnterOTPProps {
  step: string;
  nextStep: (nextStep: string, noHistory?: boolean) => void;
  phoneNumber: string;
  isReturningUserFlow: boolean;
  setCognitoAuthTokens: (tokens: any) => void;
  setRuxTokenData: (any: RuxTokenData | null) => void;
  setIsUserAuthenticated: (isAuthenticated: boolean) => void;
  previousStep: string;
  isReturningUserInSignUpFlow: boolean;
  setIsReturningUserInSignUpFlow: (
    isReturningUserInSignUpFlow: boolean
  ) => void;
  accountInfo: any;
  posthog: any;
  session: any;
}

export default function EnterOTP({
  nextStep,
  phoneNumber,
  setCognitoAuthTokens,
  setRuxTokenData,
  setIsUserAuthenticated,
  previousStep,
  isReturningUserInSignUpFlow,
  setIsReturningUserInSignUpFlow,
  accountInfo,
  posthog,
  step,
  session,
}: EnterOTPProps) {
  /* ───────── state ───────── */
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10);
  const [isResending, setIsResending] = useState(false);

  const isReturningUserFlow = previousStep === "axle-account-sign-in";

  /* ───────── refs ───────── */
  const otpInputRef = useRef<HTMLInputElement | null>(null);

  // Effect for PostHog tracking
  useEffect(() => {
    posthog.capture("$pageview", { step });
  }, [posthog, step]);

  // Update the time left every second for the resend code button
  useEffect(() => {
    if (timeLeft <= 0) return;
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft]);

  useEffect(() => {
    // Auto-focus the OTP input field when the component mounts
    if (otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, []);

  /* ───────── resend code ───────── */
  async function handleResendCode() {
    if (timeLeft > 0) return;
    setIsResending(true);
    setError("");
    try {
      if (isReturningUserFlow || isReturningUserInSignUpFlow) {
        // For returning users, use signIn with SMS_OTP
        await signIn({
          username: phoneNumber,
          options: {
            authFlowType: "USER_AUTH",
            preferredChallenge: "SMS_OTP",
            preferredFirstFactor: "SMS_OTP",
          },
        });
      } else {
        // For new users, try to resend the signup code
        try {
          await resendSignUpCode({ username: phoneNumber });
          // If resendSignUpCode succeeds, the user exists but is unconfirmed
          console.log("Resent signup code to unconfirmed user");
        } catch (resendError: any) {
          console.log("Error from resendSignUpCode:", resendError);
          // If we get an error about user being confirmed, they should be treated as returning user
          if (
            resendError.message &&
            resendError.message.includes("User is already confirmed")
          ) {
            console.log(
              "User is actually a returning user who is already confirmed"
            );
            // We can't update isReturningUserInNewUserFlow since it's passed as a prop
            // Just use signIn for confirmed users with SMS_OTP
            await signIn({
              username: phoneNumber,
              options: {
                authFlowType: "USER_AUTH",
                preferredChallenge: "SMS_OTP",
                preferredFirstFactor: "SMS_OTP",
              },
            });
          } else {
            // Some other error with resending code
            throw resendError; // Pass to outer catch block
          }
        }
      }
      setTimeLeft(10);
    } catch (err: any) {
      // Handle expected resend-code errors without logging at error level
      if (
        err.name === "LimitExceededException" ||
        err.name === "CodeDeliveryFailureException"
      ) {
        setError(
          `Failed to resend verification code. Please try again later and ${
            isReturningUserFlow ? "continue as guest" : "skip this step for now"
          }.`
        );
      } else if (err.name === "SignInException") {
        setError(
          "Unable to send verification code. Please request a new code or try signing in again."
        );
      } else if (err.name === "UserAlreadyAuthenticatedException") {
        setError(
          `User is already successfully authenticated. You are all set!`
        );
        // wait 4 seconds before going to success
        setTimeout(() => {
          nextStep(getNextStepBeforeSuccess(session));
        }, 4000);
      } else {
        // Unknown or unexpected error – log so it surfaces in Sentry
        console.error("Failed to resend code:", err.name);
        console.log(JSON.stringify(err, null, 2));

        setError("Failed to resend verification code. Please try again.");
      }
    } finally {
      setIsResending(false);
    }
  }

  /* ───────── verify code ───────── */
  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    setError("");

    if (!otp.trim()) {
      setError("Enter your verification code");
      return;
    }

    setIsSubmitting(true);
    try {
      if (isReturningUserFlow || isReturningUserInSignUpFlow) {
        // RETURNING USER FLOW
        const res = await confirmSignIn({ challengeResponse: otp });
        if (res.nextStep?.signInStep !== "DONE") {
          throw new Error("Code incorrect or additional step required");
        }

        await rememberDevice();
        const { tokens } = await fetchAuthSession();

        setCognitoAuthTokens(tokens);
        setIsUserAuthenticated(true);

        /* Only verify the device token for TRUE returning users.
           Users in the special "returningUserInSignUpFlow" path are
           signing in immediately after confirmation, so skip this step. */
        if (isReturningUserFlow) {
          const result = await verifyDeviceToken(
            tokens?.idToken?.toString() ?? "",
            session.id
          );

          // Check if result is undefined or not an array
          if (!result || !Array.isArray(result)) {
            console.error("Invalid result from verifyDeviceToken:", result);
            nextStep("axle-error");
            return;
          }

          const [data, error] = result;
          if (error || !data || !data.isValid) {
            console.error("Token validation failed:", error?.message);
            nextStep("axle-error");
            return;
          }

          setRuxTokenData(data);
        }

        if (isReturningUserInSignUpFlow) {
          // RETURNING USER GOING THROUGH SIGN UP FLOW
          // 1. Add any new accounts to the returning user's saved account list
          if (accountInfo.id) {
            await updateCognitoUserAccounts(accountInfo.id);
          }
          // 2. Go to validatePolicy OR success
          nextStep(getNextStepBeforeSuccess(session));
        } else {
          // TRUE RETURNING USER FLOW
          nextStep("welcome-back");
        }
      } else {
        // NEW USER SIGN UP FLOW
        const result = await confirmSignUp({
          username: phoneNumber,
          confirmationCode: otp,
        });

        if (result.nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
          /* Auto-sign-in handshake still pending */
          await autoSignIn();

          // Now we have a session – safe to access tokens and remember device
          await rememberDevice();

          // Update accounts using authenticated client-side utility
          if (accountInfo.id) {
            await updateCognitoUserAccounts(accountInfo.id);
          }

          const { tokens } = await fetchAuthSession({ forceRefresh: true });
          setCognitoAuthTokens(tokens);
          setIsUserAuthenticated(true);

          nextStep(getNextStepBeforeSuccess(session));
        } else {
          /* signUpStep === "DONE" → no session available. Ask user for the second OTP */
          setError(
            "For extra security, please enter the second verification code we just sent."
          );

          setIsReturningUserInSignUpFlow(true);
          await handleReturningUserSignIn(
            phoneNumber,
            nextStep,
            setError,
            setIsSubmitting
          );
        }
      }
    } catch (err: any) {
      if (err.name === "CodeMismatchException") {
        // Expected case when the user enters an incorrect code
        setError("Invalid code provided. Please try again.");
      } else if (err.name === "ExpiredCodeException") {
        // The provided OTP has expired – prompt the user to request a new one
        setError(
          "This code has expired. Please request a new code and try again."
        );
      } else if (err.name === "DeviceMetadataNotFoundException") {
        /* This can occur if Cognito is unable to locate metadata for the current device
           after a successful sign-in. We gracefully notify the user so they can try again
           without surfacing a cryptic error. */
        console.log("Device metadata not found... prompting user to try again");
        setError(
          "We couldn't verify your device. Please try signing in again."
        );
      } else if (err.name === "SignInException") {
        // Generic sign-in error from Cognito (e.g., invalid state, internal error)
        setError(
          "We were unable to sign you in. Please request a new code or try signing in again."
        );
      } else if (err.name === "NotAuthorizedException") {
        /* This occurs when the user submits a valid OTP code but the network request fails after the network request is sent.
      This leads to the user becoming a confirmed and verified user in Cognito but the frontend thinking that the user is not authenticated.
      */
        console.log(
          "User is already authenticated with a valid OTP code... sending to success"
        );
        nextStep(getNextStepBeforeSuccess(session));
      } else if (err.name === "NetworkError") {
        console.log("Network error... asking user to try again");
        setError(DEFAULT_ERROR_MESSAGE);
      } else {
        // Unknown error – log at error level so it surfaces in Sentry
        console.error("Verification failed:", err.name);
        console.log(JSON.stringify(err, null, 2));
        setError(DEFAULT_ERROR_MESSAGE);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleVerify}
      className="flex min-h-0 flex-1 flex-col phone-entry-screen"
    >
      <StepBody>
        <PhoneVerificationHeader
          phoneNumber={phoneNumber}
          isReturningUserFlow={isReturningUserFlow}
          isReturningUserInSignUpFlow={isReturningUserInSignUpFlow}
        />

        <div className="flex flex-col gap-3">
          <Field id="otp" label="Verification code" error={error}>
            <input
              id="otp"
              ref={otpInputRef}
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
              maxLength={8}
              className={cn(inputClassName, "font-mono")}
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              aria-label="Verification code"
              aria-invalid={Boolean(error)}
              aria-describedby={error ? "otp-error" : undefined}
              tabIndex={0}
            />
          </Field>

          {/* Resend code button*/}
          <ResendCodeButton
            timeLeft={timeLeft}
            isResending={isResending}
            onResend={handleResendCode}
            posthog={posthog}
          />
        </div>
      </StepBody>

      <StepFooter>
        <Button
          type="submit"
          name="continue"
          onClick={() => {
            posthog.capture("enter_otp_submit");
          }}
        >
          {isSubmitting ? "Verifying..." : "Verify"}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            posthog.capture("enter_otp_skip");
            isReturningUserFlow
              ? nextStep("carriers")
              : nextStep(getNextStepBeforeSuccess(session));
          }}
          name="skip"
          type="button"
        >
          {isReturningUserFlow ? "Continue as guest" : SKIP_PASSPORT}
        </Button>
      </StepFooter>
    </form>
  );
}
